import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Fade,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import {
  clearWebchatSession,
  getWebchatErrorCode,
  getWebchatSessionMeta,
  getWebchatToken,
  setWebchatSession,
  webchatWidgetConfig,
  webchatWidgetStart,
} from "../../api/webchat";
import WebChatPage from "./WebChatPage";

const VISITOR_PREFIX = "xproSiteWebchatVisitor:";
const RESTART_FLAG = "xproSiteWebchatRestart";

function visitorStorageKey(publicKey) {
  return `${VISITOR_PREFIX}${publicKey}`;
}

function readErrorMessage(error) {
  const code = getWebchatErrorCode(error);
  if (code === "site_webchat.domain") return "Este site não está autorizado a abrir o chat.";
  if (code === "site_webchat.name_required") return "Informe seu nome para começar.";
  if (code === "site_webchat.email_required") return "Informe um e-mail válido.";
  return error?.response?.data?.message || "Não foi possível abrir o chat agora.";
}

function BrandMark({ name, logo }) {
  const initial = String(name || "A").charAt(0).toUpperCase();
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        bgcolor: logo ? "#fff" : "rgba(255,255,255,0.22)",
        border: "2px solid rgba(255,255,255,0.35)",
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        flexShrink: 0,
        boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
        color: "#fff",
        fontWeight: 700,
        fontSize: "1.15rem",
        letterSpacing: "-0.02em",
      }}
    >
      {logo ? (
        <Box component="img" src={logo} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        initial
      )}
    </Box>
  );
}

function LoadingScreen({ accent, label = "Conectando…" }) {
  return (
    <Fade in timeout={280}>
      <Box
        sx={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.75,
          px: 3,
          background: `linear-gradient(165deg, ${accent}12 0%, #ffffff 42%, #f8fafc 100%)`,
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            bgcolor: `${accent}14`,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CircularProgress size={28} thickness={4.5} sx={{ color: accent }} />
        </Box>
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "#667085",
            letterSpacing: "0.01em",
            animation: "xproEmbedPulse 1.5s ease-in-out infinite",
            "@keyframes xproEmbedPulse": {
              "0%, 100%": { opacity: 0.55 },
              "50%": { opacity: 1 },
            },
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
            },
          }}
        >
          {label}
        </Typography>
      </Box>
    </Fade>
  );
}

const startInflight = new Map();

export default function SiteWebchatEmbedPage() {
  const [searchParams] = useSearchParams();
  const company = (searchParams.get("company") || "").trim().toLowerCase();
  const key = (searchParams.get("key") || "").trim();
  const pageHost = (searchParams.get("pageHost") || "").trim();

  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [formError, setFormError] = useState("");
  const [starting, setStarting] = useState(false);
  const [ready, setReady] = useState(false);
  const [bootStarting, setBootStarting] = useState(false);

  useEffect(() => {
    if (!company || !key) {
      setConfigError("Link do chat incompleto.");
      return undefined;
    }
    let cancelled = false;
    webchatWidgetConfig({ company, key })
      .then((cfg) => {
        if (!cancelled) setConfig(cfg || {});
      })
      .catch((err) => {
        if (!cancelled) setConfigError(readErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [company, key]);

  useEffect(() => {
    if (!config || !company || !key) return undefined;
    const existing = getWebchatToken();
    const meta = getWebchatSessionMeta();
    const sameChannel = meta?.widgetKey === key;
    const restart = sessionStorage.getItem(RESTART_FLAG) === "1";
    if (restart) sessionStorage.removeItem(RESTART_FLAG);

    if (existing && sameChannel && !restart) {
      setReady(true);
      return undefined;
    }
    if (existing && !sameChannel) clearWebchatSession();

    const prechat = config.prechatMode || "name";
    const visitorToken = window.localStorage.getItem(visitorStorageKey(key)) || "";
    if (prechat === "none" || visitorToken || restart) {
      setBootStarting(true);
      begin({ visitorToken });
    }
    return undefined;
    // begin is stable enough for this boot
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, company, key]);

  async function begin({ visitorToken, formName, formEmail } = {}) {
    const inflightKey = `${company}:${key}`;
    if (startInflight.has(inflightKey)) {
      setStarting(true);
      try {
        await startInflight.get(inflightKey);
        setReady(true);
      } catch (err) {
        setFormError(readErrorMessage(err));
        setReady(false);
      } finally {
        setStarting(false);
        setBootStarting(false);
      }
      return;
    }

    setStarting(true);
    setFormError("");
    const run = (async () => {
      const started = await webchatWidgetStart({
        company,
        key,
        pageHost,
        name: formName || "",
        email: formEmail || "",
        visitorToken: visitorToken || window.localStorage.getItem(visitorStorageKey(key)) || "",
      });
      if (started?.visitorToken) {
        window.localStorage.setItem(visitorStorageKey(key), started.visitorToken);
      }
      setWebchatSession({
        token: started.token,
        company: started.company || company,
        meta: { ...started, widgetKey: key },
      });
      setReady(true);
    })();
    startInflight.set(inflightKey, run);
    try {
      await run;
    } catch (err) {
      const code = getWebchatErrorCode(err);
      if (code === "site_webchat.name_required" || code === "site_webchat.email_required") {
        window.localStorage.removeItem(visitorStorageKey(key));
      }
      setFormError(readErrorMessage(err));
      setReady(false);
    } finally {
      startInflight.delete(inflightKey);
      setStarting(false);
      setBootStarting(false);
    }
  }

  const accent = config?.accentColor || "#0F6FDE";
  const brandName = useMemo(
    () => config?.branding?.companyName || config?.channelName || company || "Atendimento",
    [config, company]
  );
  const brandLogo = config?.branding?.logo || null;

  if (configError) {
    return (
      <Box
        sx={{
          minHeight: "100dvh",
          display: "grid",
          placeItems: "center",
          p: 3,
          bgcolor: "#f8fafc",
        }}
      >
        <Alert severity="warning" sx={{ borderRadius: 2, maxWidth: 320 }}>
          {configError}
        </Alert>
      </Box>
    );
  }

  if (!config || bootStarting) {
    return <LoadingScreen accent={accent} label={!config ? "Preparando…" : "Abrindo conversa…"} />;
  }

  const storedVisitor =
    typeof window !== "undefined" && key ? window.localStorage.getItem(visitorStorageKey(key)) : "";
  const restarting =
    typeof window !== "undefined" && sessionStorage.getItem(RESTART_FLAG) === "1";
  const askIdentity = (config.prechatMode || "name") !== "none" && !storedVisitor && !restarting;

  if (!ready && askIdentity) {
    const needsEmail = config.prechatMode === "name_email";
    return (
      <Box
        sx={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <Box
          sx={{
            position: "relative",
            px: 2.75,
            pt: 2.75,
            pb: 3.5,
            color: "#fff",
            background: `linear-gradient(145deg, ${accent} 0%, ${accent}dd 55%, ${accent}bb 100%)`,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(ellipse 80% 60% at 100% -10%, rgba(255,255,255,0.22), transparent 55%)",
              pointerEvents: "none",
            }}
          />
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ position: "relative" }}>
            <BrandMark name={brandName} logo={brandLogo} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.35rem",
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                }}
              >
                {config.greetingTitle || "Olá"}
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: "0.92rem", opacity: 0.94, lineHeight: 1.4 }}>
                {config.greetingMessage || "Como podemos ajudar?"}
              </Typography>
            </Box>
          </Stack>
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: -1,
              height: 18,
              bgcolor: "#fff",
              borderTopLeftRadius: 18,
              borderTopRightRadius: 18,
            }}
          />
        </Box>

        <Box
          component="form"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            px: 2.75,
            pt: 0.5,
            pb: 2.5,
          }}
          onSubmit={(event) => {
            event.preventDefault();
            begin({ formName: name.trim(), formEmail: email.trim() });
          }}
        >
          <Stack spacing={2} sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "0.8125rem", color: "#667085", lineHeight: 1.45 }}>
              Preencha para iniciar o atendimento. Leva só alguns segundos.
            </Typography>
            <TextField
              autoFocus
              required
              label="Seu nome"
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
              disabled={starting}
              inputProps={{ autoComplete: "name" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2.5,
                  bgcolor: "#f8fafc",
                  transition: "background .2s ease, box-shadow .2s ease",
                  "&:hover": { bgcolor: "#fff" },
                  "&.Mui-focused": {
                    bgcolor: "#fff",
                    boxShadow: `0 0 0 3px ${accent}22`,
                  },
                  "& fieldset": { borderColor: "#e4e7ec" },
                  "&:hover fieldset": { borderColor: "#cfd4dc" },
                  "&.Mui-focused fieldset": { borderColor: accent, borderWidth: 1.5 },
                },
              }}
            />
            {needsEmail ? (
              <TextField
                required
                type="email"
                label="Seu e-mail"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                fullWidth
                disabled={starting}
                inputProps={{ autoComplete: "email" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5,
                    bgcolor: "#f8fafc",
                    "&.Mui-focused": {
                      bgcolor: "#fff",
                      boxShadow: `0 0 0 3px ${accent}22`,
                    },
                    "& fieldset": { borderColor: "#e4e7ec" },
                    "&.Mui-focused fieldset": { borderColor: accent, borderWidth: 1.5 },
                  },
                }}
              />
            ) : null}
            {formError ? (
              <Alert severity="error" sx={{ borderRadius: 2, py: 0.5 }}>
                {formError}
              </Alert>
            ) : null}
            <Box sx={{ flex: 1, minHeight: 12 }} />
            <Button
              type="submit"
              variant="contained"
              disabled={starting || !name.trim() || (needsEmail && !email.trim())}
              fullWidth
              sx={{
                bgcolor: accent,
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                letterSpacing: "0.01em",
                py: 1.35,
                borderRadius: 2.5,
                boxShadow: `0 6px 16px ${accent}40`,
                transition: "transform .18s ease, box-shadow .18s ease, background .18s ease",
                "&:hover": {
                  bgcolor: accent,
                  filter: "brightness(0.96)",
                  boxShadow: `0 8px 20px ${accent}48`,
                },
                "&:active": { transform: "scale(0.985)" },
                "&.Mui-disabled": {
                  bgcolor: `${accent}66`,
                  color: "#fff",
                },
              }}
            >
              {starting ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />
                  <span>Abrindo…</span>
                </Stack>
              ) : (
                "Começar conversa"
              )}
            </Button>
            <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ pt: 0.5 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: "#12B76A",
                  boxShadow: "0 0 0 3px rgba(18,183,106,0.18)",
                }}
              />
              <Typography sx={{ fontSize: "0.75rem", color: "#98a2b3", fontWeight: 500 }}>
                Equipe disponível
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>
    );
  }

  if (!ready) {
    return formError ? (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", p: 3, bgcolor: "#f8fafc" }}>
        <Alert severity="error" sx={{ borderRadius: 2, maxWidth: 320 }}>
          {formError}
        </Alert>
      </Box>
    ) : (
      <LoadingScreen accent={accent} label="Abrindo conversa…" />
    );
  }

  return <WebChatPage />;
}
